// «В контент-план» в карточке поста: лента, избранное, разбор по ссылкам.
// Пост в плане — «В плане · Открыть». «▾» — выбор доски всегда: так текст из
// Telegram кладут на доску Instagram идеей для рилса.

import { ChevronDown, SquareKanban } from 'lucide-react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { planError, useAddFromPost, usePlanOverview, usePostPlan } from '../../api/plan'
import type { Board, PlanPlatform, PostDetail, PostPlanCard } from '../../api/types'
import { cardHref } from '../../lib/plan/url'
import { useIsDesktop } from '../../lib/useIsDesktop'
import { Dialog } from '../Dialog'
import { Popover } from '../Popover'
import { Spinner } from '../Spinner'
import { toast } from '../Toast'

const TITLE: Record<PlanPlatform, string> = { telegram: 'Telegram', instagram: 'Instagram' }

/** Рилс — в Instagram, всё из «Текстов» — в Telegram. */
function defaultPlatform(post: Pick<PostDetail, 'platform' | 'kind'>): PlanPlatform {
  return post.platform === 'instagram' && post.kind === 'reel' ? 'instagram' : 'telegram'
}

export function AddToPlanButton({ post }: { post: PostDetail }) {
  const desktop = useIsDesktop()
  const navigate = useNavigate()
  const plan = usePostPlan(post.id)
  const overview = usePlanOverview()
  const add = useAddFromPost()
  const [picker, setPicker] = useState(false)
  const anchor = useRef<HTMLDivElement | null>(null)
  const cards = plan.data?.cards ?? []
  const home = defaultPlatform(post)
  const boards = (overview.data?.boards ?? []).filter((b) => !b.archived)
  const homeBoards = boards.filter((b) => b.platform === home)
  // Бэкенда плана ещё нет (или он лежит) — кнопки нет, карточка поста живёт как раньше.
  if (overview.isError || plan.isError) return null

  const open = (c: Pick<PostPlanCard, 'platform' | 'board_id' | 'id'>) => {
    setPicker(false)
    void navigate(cardHref(c.platform, c.board_id, c.id))
  }

  const addTo = (board: Board) => {
    setPicker(false)
    add.mutate(
      { post_id: post.id, board_id: board.id },
      {
        onSuccess: ({ card, created }) => {
          const idea = overview.data?.stages[board.platform]?.find((s) => s.key === 'idea')?.title ?? 'Идея'
          toast(created ? `Добавлено в «${idea}»` : 'Уже в плане', {
            action: { label: 'Открыть', onClick: () => open({ platform: card.platform, board_id: card.board_id, id: card.id }) },
          })
        },
        onError: (err) => toast(`Не добавилось: ${planError(err)}`, { error: true, ms: 4000 }),
      },
    )
  }

  const main = () => {
    if (cards.length === 1 && cards[0]) return open(cards[0])
    if (cards.length > 1) return setPicker(true)
    if (homeBoards.length === 1 && homeBoards[0]) return addTo(homeBoards[0])
    setPicker(true)
  }

  const busy = add.isPending || overview.isPending || plan.isPending
  const inPlan = cards.length > 0
  const list = <BoardPicker post={post} boards={boards} home={home} cards={cards} onAdd={addTo} onOpen={open} compact={desktop} />

  return (
    <div ref={anchor} className="flex items-stretch">
      <button
        type="button"
        onClick={main}
        disabled={busy}
        className={`inline-flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-l-lg px-3 text-sm font-medium ring-1 ring-line ring-inset transition-colors disabled:opacity-60 desk:h-8 desk:flex-none desk:text-[13px] ${
          inPlan ? 'bg-surface text-fg hover:bg-line/60' : 'bg-accent text-accent-fg ring-accent hover:opacity-90'
        }`}
      >
        {add.isPending ? <Spinner size={15} className={inPlan ? '' : 'text-accent-fg'} /> : <SquareKanban size={16} />}
        <span className="truncate">{inPlan ? 'В плане · Открыть' : 'В контент-план'}</span>
      </button>
      <button
        type="button"
        onClick={() => setPicker((v) => !v)}
        disabled={busy}
        aria-label="Выбрать доску"
        aria-expanded={picker}
        className={`-ml-px inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-r-lg ring-1 ring-line ring-inset transition-colors disabled:opacity-60 desk:h-8 desk:w-8 ${
          inPlan ? 'bg-surface text-fg hover:bg-line/60' : 'bg-accent text-accent-fg ring-accent hover:opacity-90'
        }`}
      >
        <ChevronDown size={16} />
      </button>
      {picker &&
        (desktop ? (
          <Popover anchor={anchor} onClose={() => setPicker(false)} label="В контент-план" width={300}>
            <div className="border-b border-line px-3 py-2 text-[13px] font-semibold">В контент-план</div>
            <div className="overflow-y-auto">{list}</div>
          </Popover>
        ) : (
          <Dialog title="В контент-план" onClose={() => setPicker(false)}>
            {list}
          </Dialog>
        ))}
    </div>
  )
}

/** Доски площадки по умолчанию сверху, остальные — под заголовком площадки. */
function BoardPicker({
  post,
  boards,
  home,
  cards,
  onAdd,
  onOpen,
  compact,
}: {
  post: PostDetail
  boards: Board[]
  home: PlanPlatform
  cards: PostPlanCard[]
  onAdd: (b: Board) => void
  onOpen: (c: PostPlanCard) => void
  compact: boolean
}) {
  const order: PlanPlatform[] = home === 'instagram' ? ['instagram', 'telegram'] : ['telegram', 'instagram']
  const row = compact ? 'min-h-10 px-3' : 'min-h-12 px-1'
  if (boards.length === 0) return <p className="p-3 text-sm text-muted">Досок пока нет.</p>
  return (
    <div className={compact ? 'py-1' : '-mx-1 pb-1'}>
      {order.map((platform, gi) => {
        const list = boards.filter((b) => b.platform === platform).sort((a, b) => a.position - b.position || a.id - b.id)
        if (list.length === 0) return null
        return (
          <div key={platform}>
            {(gi > 0 || compact) && (
              <h4 className={`pt-2 pb-1 text-xs font-medium tracking-wide text-muted uppercase ${compact ? 'px-3' : 'px-1'}`}>
                {TITLE[platform]}
                {platform !== defaultPlatform(post) && post.platform === 'telegram' && platform === 'instagram' && ' · идея для рилса'}
              </h4>
            )}
            <ul>
              {list.map((b) => {
                const there = cards.find((c) => c.board_id === b.id)
                return (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => (there ? onOpen(there) : onAdd(b))}
                      className={`flex w-full items-center gap-3 rounded-lg text-left hover:bg-surface ${row}`}
                    >
                      <span className="min-w-0 flex-1 truncate text-sm">{b.title}</span>
                      {there ? (
                        <span className="shrink-0 text-xs text-muted">
                          Уже здесь · {there.stage_title} · <span className="font-medium text-link">Открыть</span>
                        </span>
                      ) : (
                        <span className="shrink-0 text-xs font-medium text-link">Добавить</span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </div>
  )
}
